import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import PlatformOverviewAndCore from './pages/docs/PlatformOverviewAndCore';
import TechnicalImplementation from './pages/docs/TechnicalImplementation';
import AdvancedFeatures from './pages/docs/AdvancedFeatures';
import FutureProofing from './pages/docs/FutureProofing';
import IncidentResponse from './pages/docs/IncidentResponse';
import SecurityOperations from './pages/docs/SecurityOperations';
import ContinuousImprovement from './pages/docs/ContinuousImprovement';
import ImplementationDeployment from './pages/docs/ImplementationDeployment';
import IntegrationCapabilities from './pages/docs/IntegrationCapabilities';
import MonitoringMaintenance from './pages/docs/MonitoringMaintenance';
import NistCsf from './pages/docs/NistCsf';
import Scans from './pages/Scans';
import ScanDetail from './pages/ScanDetail';
import NetworkAnalyzer from './pages/NetworkAnalyzer';
import Onboarding from './pages/Onboarding';
import ExternalAssets from './pages/ExternalAssets';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ResendVerification from './pages/ResendVerification';
import Home from './pages/Home';
import Pricing from './pages/Pricing';
import AdminUsers from './pages/AdminUsers';
import AdminUserDetail from './pages/AdminUserDetail';
import AdminTraffic from './pages/AdminTraffic';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Agents from './pages/Agents';
import DebugAgents from './pages/DebugAgents';
import Reports from './pages/Reports';
import Alerts from './pages/Alerts';
import Workflows from './pages/features/Workflows';
import Response from './pages/features/Response';
import Integrations from './pages/features/Integrations';
import PlatformOverview from './pages/PlatformOverview';
import Coverage from './pages/Coverage';
import Tuning from './pages/Tuning';
import MspDashboard from './pages/msp/MspDashboard';
import Tenants from './pages/msp/Tenants';
import TenantDetail from './pages/msp/TenantDetail';
import TenantAssets from './pages/msp/TenantAssets';
import TenantExternalScans from './pages/msp/TenantExternalScans';
import ExternalScanDetail from './pages/msp/ExternalScanDetail';
import TenantFindings from './pages/msp/TenantFindings';
import TenantAlertsTimeline from './pages/msp/TenantAlertsTimeline';
import TenantReports from './pages/msp/TenantReports';
import TenantReportDetail from './pages/msp/TenantReportDetail';
import MspBranding from './pages/msp/MspBranding';
import ExternalScansOverview from './pages/ExternalScansOverview';
import { TenantProvider } from './context/TenantContext';
import { getAuthState } from './services/auth';
import './App.css';

function PrivateRoute({ children }) {
  const { token } = getAuthState();
  return token ? children : <Navigate to="/home" />;
}

function AdminRoute({ children }) {
  const { token, user } = getAuthState();
  
  if (!token) return <Navigate to="/login" />;
  if (user.role !== 'admin') return <Navigate to="/" />;
  
  return children;
}

function MspRoute({ children }) {
  const { token, role } = getAuthState();
  const allowedRoles = [
    'sentinel',
    'admin',
    'overdrive',
    'nexus'
  ];

  if (!token) return <Navigate to="/login" />;

  const normalizedRole = (role || '').toLowerCase();
  if (!allowedRoles.includes(normalizedRole)) return <Navigate to="/" />;

  return children;
}

function MainContent({ isAuthenticated }) {
  const location = useLocation();
  const isHomePage = location.pathname === '/home';
  
  return (
    <main className={isAuthenticated && !isHomePage ? "main-content" : ""}>
      <Routes>
        <Route path="/home" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/resend-verification" element={<ResendVerification />} />
        <Route path="/features/workflows" element={<Workflows />} />
        <Route path="/features/response" element={<Response />} />
        <Route path="/features/integrations" element={<Integrations />} />
        <Route path="/platform" element={<PlatformOverview />} />
        <Route path="/platform/coverage" element={<Coverage />} />
        <Route path="/platform/tuning" element={<Tuning />} />
        <Route path="/pricing" element={<Pricing />} />
        {/* Documentation Dropdown Pages */}
        <Route path="/docs/platform-overview" element={<PlatformOverviewAndCore />} />
        <Route path="/docs/technical-implementation" element={<TechnicalImplementation />} />
        <Route path="/docs/advanced-features" element={<AdvancedFeatures />} />
        <Route path="/docs/future-proofing" element={<FutureProofing />} />
        <Route path="/docs/incident-response" element={<IncidentResponse />} />
        <Route path="/docs/security-operations" element={<SecurityOperations />} />
        <Route path="/docs/continuous-improvement" element={<ContinuousImprovement />} />
        <Route path="/docs/nist-csf" element={<NistCsf />} />
        <Route path="/docs/implementation-deployment" element={<ImplementationDeployment />} />
        <Route path="/docs/integration-capabilities" element={<IntegrationCapabilities />} />
        <Route path="/docs/monitoring-maintenance" element={<MonitoringMaintenance />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Scans />
            </PrivateRoute>
          }
        />
        <Route
          path="/scans"
          element={
            <PrivateRoute>
              <Scans />
            </PrivateRoute>
          }
        />
        <Route
          path="/msp"
          element={
            <MspRoute>
              <MspDashboard />
            </MspRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <PrivateRoute>
              <Onboarding />
            </PrivateRoute>
          }
        />
        {/* Redirect old /msp/clinics URLs to /msp/tenants */}
        <Route path="/msp/clinics" element={<Navigate to="/msp/tenants" replace />} />
        <Route path="/msp/clinics/*" element={<Navigate to="/msp/tenants" replace />} />
        <Route
          path="/msp/tenants"
          element={
            <MspRoute>
              <Tenants />
            </MspRoute>
          }
        />
        <Route
          path="/msp/tenants/:tenantId"
          element={
            <MspRoute>
              <TenantDetail />
            </MspRoute>
          }
        />
        <Route
          path="/msp/tenants/:tenantId/assets"
          element={
            <MspRoute>
              <TenantAssets />
            </MspRoute>
          }
        />
        <Route
          path="/msp/tenants/:tenantId/external-scans"
          element={
            <MspRoute>
              <TenantExternalScans />
            </MspRoute>
          }
        />
        <Route
          path="/msp/tenants/:tenantId/external-scans/:scanId"
          element={
            <MspRoute>
              <ExternalScanDetail />
            </MspRoute>
          }
        />
        <Route
          path="/msp/tenants/:tenantId/findings"
          element={
            <MspRoute>
              <TenantFindings />
            </MspRoute>
          }
        />
        <Route
          path="/msp/tenants/:tenantId/alerts"
          element={
            <MspRoute>
              <TenantAlertsTimeline />
            </MspRoute>
          }
        />
        <Route
          path="/msp/tenants/:tenantId/reports"
          element={
            <MspRoute>
              <TenantReports />
            </MspRoute>
          }
        />
        <Route
          path="/msp/tenants/:tenantId/reports/:reportId"
          element={
            <MspRoute>
              <TenantReportDetail />
            </MspRoute>
          }
        />
        <Route
          path="/msp/branding"
          element={
            <MspRoute>
              <MspBranding />
            </MspRoute>
          }
        />
        <Route
          path="/external-scans"
          element={
            <PrivateRoute>
              <ExternalScansOverview />
            </PrivateRoute>
          }
        />
        <Route
          path="/network-analyzer"
          element={
            <PrivateRoute>
              <NetworkAnalyzer />
            </PrivateRoute>
          }
        />
        <Route
          path="/external-assets"
          element={
            <PrivateRoute>
              <ExternalAssets />
            </PrivateRoute>
          }
        />
        <Route
          path="/agents"
          element={
            <PrivateRoute>
              <Agents />
            </PrivateRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <PrivateRoute>
              <Reports />
            </PrivateRoute>
          }
        />
        <Route
          path="/alerts"
          element={
            <PrivateRoute>
              <Alerts />
            </PrivateRoute>
          }
        />
        <Route
          path="/debug/agents"
          element={
            <PrivateRoute>
              <DebugAgents />
            </PrivateRoute>
          }
        />
        <Route
          path="/scans/:scanId"
          element={
            <PrivateRoute>
              <ScanDetail />
            </PrivateRoute>
          }
        />
        <Route
          path="/blog"
          element={
            <PrivateRoute>
              <Blog />
            </PrivateRoute>
          }
        />
        <Route
          path="/blog/:postId"
          element={
            <PrivateRoute>
              <BlogPost />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <AdminUsers />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users/:userId"
          element={
            <AdminRoute>
              <AdminUserDetail />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/traffic"
          element={
            <AdminRoute>
              <AdminTraffic />
            </AdminRoute>
          }
        />
      </Routes>
    </main>
  );
}

function App() {
  const [authState, setAuthState] = useState(getAuthState());

  useEffect(() => {
    const checkAuth = () => {
      setAuthState(getAuthState());
    };

    // Listen for storage changes to update auth state across tabs/windows
    window.addEventListener('storage', checkAuth);
    // Listen for custom auth-change event from within the same tab
    window.addEventListener('auth-change', checkAuth);
    
    // Initial check
    checkAuth();

    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('auth-change', checkAuth);
    };
  }, []);

  const isAuthenticated = authState.isAuthenticated;

  return (
    <Router
      basename={process.env.PUBLIC_URL}
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <AppContent isAuthenticated={isAuthenticated} authState={authState} />
    </Router>
  );
}

function AppContent({ isAuthenticated, authState }) {
  const useGithubTheme = true;

  return (
    <div className={`App ${useGithubTheme ? 'theme-github' : ''}`}>
      <TenantProvider isAuthenticated={isAuthenticated} accountType={authState.accountType}>
        <Navbar />
        <MainContent isAuthenticated={isAuthenticated} />
      </TenantProvider>
    </div>
  );
}

export default App;
